import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import { ArrowLeft, MessageSquare, Plus, Users, Mic, Lock } from 'lucide-react'

const GOLD = '#c9a84c'
const RED = '#b8441a'

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000
  if (diff < 60) return 'just now'
  if (diff < 3600) return Math.floor(diff / 60) + 'm ago'
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago'
  if (diff < 2592000) return Math.floor(diff / 86400) + 'd ago'
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const cardStyle = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px',
}

const labelStyle = {
  display: 'block', fontFamily: 'monospace', fontSize: '0.67rem', fontWeight: '500',
  textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.35rem',
}

const inputStyle = {
  background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: '4px',
  padding: '0.55rem 0.75rem', fontSize: '0.88rem', fontFamily: 'DM Sans, sans-serif',
  color: 'var(--text)', width: '100%', boxSizing: 'border-box', outline: 'none',
}

const primaryBtnStyle = (disabled) => ({
  background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', padding: '0.55rem 1.1rem',
  borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: '500',
  cursor: disabled ? 'not-allowed' : 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em',
  opacity: disabled ? 0.6 : 1,
})

const backBtnStyle = {
  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
  display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: 'monospace', fontSize: '0.72rem',
  padding: '0.3rem 0', marginBottom: '1rem',
}

function VoiceTag() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontFamily: 'monospace', fontSize: '0.58rem', color: GOLD, background: 'rgba(201,168,76,0.1)', padding: '0.12rem 0.4rem', borderRadius: '99px', marginLeft: '0.5rem' }}>
      <Mic size={9} /> voice
    </span>
  )
}

export default function Forum({ session }) {
  const userId = session?.user?.id

  const [view, setView] = useState('rooms') // 'rooms' | 'room' | 'post'
  const [rooms, setRooms] = useState([])
  const [userRank, setUserRank] = useState(0)
  const [lockedMessage, setLockedMessage] = useState('')
  const [selectedRoom, setSelectedRoom] = useState(null)
  const [posts, setPosts] = useState([])
  const [sortMode, setSortMode] = useState('active') // 'active' | 'newest' | 'oldest'
  const [selectedPost, setSelectedPost] = useState(null)
  const [replies, setReplies] = useState([])

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [posting, setPosting] = useState(false)

  const [showComposer, setShowComposer] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newBody, setNewBody] = useState('')
  const [newReply, setNewReply] = useState('')

  const loadRooms = useCallback(async () => {
    setLoading(true); setError('')
    const { data, error } = await supabase
      .from('forum_rooms')
      .select('*')
      .eq('is_active', true)
      .order('rank', { ascending: true })
    if (error) { setError(error.message); setLoading(false); return }
    setRooms(data || [])

    if (userId) {
      const { data: membership } = await supabase
        .from('memberships')
        .select('tier')
        .eq('user_id', userId)
        .single()
      const myRoom = (data || []).find(r => r.tier === membership?.tier)
      setUserRank(myRoom?.rank || 0)
    }
    setLoading(false)
  }, [userId])

  useEffect(() => { loadRooms() }, [loadRooms])

  function tryOpenRoom(room) {
    if (room.rank <= userRank) {
      setLockedMessage('')
      openRoom(room)
    } else {
      setLockedMessage(`🔒 ${room.display_name} is for members at that level and above — you can't view its topics or discussions yet. Upgrade your club membership to unlock it.`)
    }
  }

  function applySort(query, mode) {
    if (mode === 'newest') return query.order('created_at', { ascending: false })
    if (mode === 'oldest') return query.order('created_at', { ascending: true })
    // 'active' (default): most participants first, then most replies, then newest
    return query
      .order('participant_count', { ascending: false })
      .order('reply_count', { ascending: false })
      .order('created_at', { ascending: false })
  }

  async function fetchPosts(room, mode) {
    let query = supabase.from('forum_posts').select('*').eq('room_tier', room.tier)
    query = applySort(query, mode)
    return query
  }

  async function openRoom(room) {
    setSelectedRoom(room)
    setView('room')
    setLoading(true); setError('')
    const { data, error } = await fetchPosts(room, sortMode)
    if (error) setError(error.message)
    else setPosts(data || [])
    setLoading(false)
  }

  async function refreshPosts(mode = sortMode) {
    if (!selectedRoom) return
    const { data, error } = await fetchPosts(selectedRoom, mode)
    if (!error) setPosts(data || [])
  }

  async function changeSortMode(mode) {
    setSortMode(mode)
    setLoading(true)
    await refreshPosts(mode)
    setLoading(false)
  }

  async function openPost(post) {
    setSelectedPost(post)
    setView('post')
    setLoading(true); setError('')
    const { data, error } = await supabase
      .from('forum_replies')
      .select('*')
      .eq('post_id', post.id)
      .order('created_at', { ascending: true })
    if (error) setError(error.message)
    else setReplies(data || [])
    setLoading(false)
  }

  async function refreshReplies() {
    if (!selectedPost) return
    const { data, error } = await supabase
      .from('forum_replies')
      .select('*')
      .eq('post_id', selectedPost.id)
      .order('created_at', { ascending: true })
    if (!error) setReplies(data || [])
  }

  function backToRooms() {
    setView('rooms'); setSelectedRoom(null); setPosts([]); setShowComposer(false); setError(''); setLockedMessage('')
  }

  function backToRoom() {
    setView('room'); setSelectedPost(null); setReplies([]); setError('')
    refreshPosts()
  }

  async function submitPost() {
    if (!newTitle.trim() || !newBody.trim()) { setError('Please add both a title and a message'); return }
    setPosting(true); setError('')
    const { error } = await supabase.from('forum_posts').insert({
      room_tier: selectedRoom.tier,
      user_id: userId,
      title: newTitle.trim(),
      body: newBody.trim(),
      source: 'text',
    })
    if (error) { setError(error.message); setPosting(false); return }
    setNewTitle(''); setNewBody(''); setShowComposer(false)
    await refreshPosts()
    setPosting(false)
  }

  async function submitReply() {
    if (!newReply.trim()) return
    setPosting(true); setError('')
    const { error } = await supabase.from('forum_replies').insert({
      post_id: selectedPost.id,
      user_id: userId,
      body: newReply.trim(),
      source: 'text',
    })
    if (error) { setError(error.message); setPosting(false); return }
    setNewReply('')
    await refreshReplies()
    setPosting(false)
  }

  function authorLabel(postUserId) {
    if (postUserId === userId) return 'You'
    return `A ${selectedRoom?.display_name || 'club'} member`
  }

  // ──────────────────────────────────────────────────────────
  // Rooms list
  // ──────────────────────────────────────────────────────────
  if (view === 'rooms') {
    return (
      <div>
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.3rem', fontWeight: '700' }}>Clubhouse</div>
          <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
            Connect with members at your club level and below
          </div>
        </div>

        {error && <div style={{ color: RED, fontFamily: 'monospace', fontSize: '0.75rem', marginBottom: '1rem' }}>⚠ {error}</div>}

        {lockedMessage && (
          <div style={{ background: 'rgba(184,68,26,0.08)', border: '1px solid rgba(184,68,26,0.25)', borderRadius: '8px', padding: '0.85rem 1.1rem', marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem' }}>
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: RED, lineHeight: 1.5 }}>{lockedMessage}</span>
            <button onClick={() => setLockedMessage('')} style={{ background: 'none', border: 'none', color: RED, cursor: 'pointer', flexShrink: 0, fontSize: '1rem', lineHeight: 1 }}>✕</button>
          </div>
        )}

        {loading ? (
          <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-muted)' }}>Loading rooms...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {rooms.map(room => {
              const locked = room.rank > userRank
              return (
                <button
                  key={room.tier}
                  onClick={() => tryOpenRoom(room)}
                  style={{ ...cardStyle, padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', textAlign: 'left', width: '100%', opacity: locked ? 0.55 : 1 }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: 'Georgia,serif', fontSize: '1rem', fontWeight: '700', color: 'var(--text)' }}>
                      {room.display_name}
                      {locked && <Lock size={13} color="var(--text-muted)" />}
                    </div>
                    {room.description && (
                      <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{room.description}</div>
                    )}
                    {locked && (
                      <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: GOLD, marginTop: '0.25rem' }}>Members only — upgrade to unlock</div>
                    )}
                  </div>
                  {locked ? <Lock size={18} color="var(--text-muted)" style={{ flexShrink: 0 }} /> : <MessageSquare size={18} color={GOLD} style={{ flexShrink: 0 }} />}
                </button>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // ──────────────────────────────────────────────────────────
  // Room view — list of posts
  // ──────────────────────────────────────────────────────────
  if (view === 'room') {
    return (
      <div>
        <button onClick={backToRooms} style={backBtnStyle}>
          <ArrowLeft size={14} /> All Rooms
        </button>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.2rem', fontWeight: '700' }}>{selectedRoom.display_name}</div>
          <button onClick={() => setShowComposer(v => !v)} style={primaryBtnStyle(false)}>
            <Plus size={13} style={{ marginRight: '0.3rem', verticalAlign: 'text-bottom' }} /> New Post
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.85rem' }}>
          <select
            value={sortMode}
            onChange={e => changeSortMode(e.target.value)}
            style={{ background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: '4px', padding: '0.35rem 0.6rem', fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text)', cursor: 'pointer' }}
          >
            <option value="active">Most Active</option>
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>

        {error && <div style={{ color: RED, fontFamily: 'monospace', fontSize: '0.75rem', marginBottom: '1rem' }}>⚠ {error}</div>}

        {showComposer && (
          <div style={{ ...cardStyle, padding: '1.1rem 1.25rem', marginBottom: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <div>
              <label style={labelStyle}>Title</label>
              <input style={inputStyle} value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="What's this about?" />
            </div>
            <div>
              <label style={labelStyle}>Message</label>
              <textarea style={{ ...inputStyle, minHeight: '90px', resize: 'vertical', fontFamily: 'DM Sans, sans-serif' }} value={newBody} onChange={e => setNewBody(e.target.value)} placeholder="Share your thoughts..." />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowComposer(false)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-muted)', padding: '0.5rem 1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.72rem', cursor: 'pointer' }}>Cancel</button>
              <button onClick={submitPost} disabled={posting} style={primaryBtnStyle(posting)}>{posting ? 'Posting...' : 'Post'}</button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-muted)' }}>Loading posts...</div>
        ) : posts.length === 0 ? (
          <div style={{ ...cardStyle, padding: '2.5rem 1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>💬</div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>No posts yet — be the first to start a conversation here.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {posts.map(post => (
              <button
                key={post.id}
                onClick={() => openPost(post)}
                style={{ ...cardStyle, padding: '1rem 1.25rem', cursor: 'pointer', textAlign: 'left', width: '100%' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontFamily: 'Georgia,serif', fontSize: '0.95rem', fontWeight: '700', color: 'var(--text)' }}>
                  {post.title}
                  {post.source === 'voice' && <VoiceTag />}
                </div>
                <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.3rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {post.body}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem', fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)' }}>
                  <span>{authorLabel(post.user_id)}</span>
                  <span>·</span>
                  <span>{timeAgo(post.created_at)}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginLeft: 'auto', color: GOLD }}>
                    <Users size={11} /> {post.participant_count}
                  </span>
                  <span>{post.reply_count} {post.reply_count === 1 ? 'reply' : 'replies'}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  // ──────────────────────────────────────────────────────────
  // Post detail view — full post + replies
  // ──────────────────────────────────────────────────────────
  return (
    <div>
      <button onClick={backToRoom} style={backBtnStyle}>
        <ArrowLeft size={14} /> {selectedRoom.display_name}
      </button>

      <div style={{ ...cardStyle, padding: '1.25rem 1.5rem', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: 'Georgia,serif', fontSize: '1.15rem', fontWeight: '700' }}>
          {selectedPost.title}
          {selectedPost.source === 'voice' && <VoiceTag />}
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
          {authorLabel(selectedPost.user_id)} · {timeAgo(selectedPost.created_at)}
        </div>
        <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', color: 'var(--text)', marginTop: '0.85rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {selectedPost.body}
        </div>
      </div>

      {error && <div style={{ color: RED, fontFamily: 'monospace', fontSize: '0.75rem', marginBottom: '1rem' }}>⚠ {error}</div>}

      <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>
        {replies.length} {replies.length === 1 ? 'Reply' : 'Replies'}
      </div>

      {loading ? (
        <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-muted)' }}>Loading replies...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.25rem' }}>
          {replies.map(reply => (
            <div key={reply.id} style={{ ...cardStyle, padding: '0.8rem 1.1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>
                <span style={{ fontWeight: '600', color: 'var(--text)' }}>{authorLabel(reply.user_id)}</span>
                <span>· {timeAgo(reply.created_at)}</span>
                {reply.source === 'voice' && <VoiceTag />}
              </div>
              <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', color: 'var(--text)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{reply.body}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ ...cardStyle, padding: '0.9rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        <label style={labelStyle}>Add a reply</label>
        <textarea style={{ ...inputStyle, minHeight: '70px', resize: 'vertical', fontFamily: 'DM Sans, sans-serif' }} value={newReply} onChange={e => setNewReply(e.target.value)} placeholder="Write your reply..." />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={submitReply} disabled={posting || !newReply.trim()} style={primaryBtnStyle(posting || !newReply.trim())}>{posting ? 'Sending...' : 'Reply'}</button>
        </div>
      </div>
    </div>
  )
}
